import browser from 'webextension-polyfill';
import store from '../store/store';  // Keep this import to ensure store is initialized
// Dev-only MCP bridge client. Vite replaces import.meta.env.MODE with a string
// literal at build time. "vite build --mode development" → "development" → true.
// "vite build" (production) → "production" → false; Rollup tree-shakes the module.
import { startMcpDevClient } from './mcpDevClient';
import { openTabvana } from '../main/openTabvana';
import { loadPrefsFromDB } from '../db/prefsLoader';
import { registerDBSyncListeners, syncInitialState, setTitleOverride } from '../db/dbSync';
import { initQueueProcessor, isQueueProcessing } from './queueProcessor';
import { clearAllProgress } from '../db/progress';
import { enqueueOperation } from '../db/queue';
import '../db/backup'; // Register backup listeners
import { logger } from '../utils/logger';
import { normalizeUrl } from '../chrome/normalizeUrl';
import { shouldIgnore } from '../db/urlNormalizationPrefs';
import { db } from '../db/db';
import { getTabvanaWindowId } from '../db/prefs';

// After an extension reload the popup window stays open but its tab goes blank
// because extension pages are torn down. Re-navigate the tab to main.html so
// the UI appears again without the user having to reopen the window.
async function maybeRestoreMainWindow() {
    const windowId = await getTabvanaWindowId();
    if (windowId == null) return;
    try {
        const win = await browser.windows.get(windowId, { populate: true });
        if (!win.tabs || win.tabs.length === 0) return;
        const tab = win.tabs[0];
        const mainUrl = browser.runtime.getURL('src/main/main.html');
        if (tab.id != null && tab.url !== mainUrl) {
            await browser.tabs.update(tab.id, { url: mainUrl });
            logger.info('[Background] Restored main window tab after extension reload');
        }
    } catch (_e) {
        // Window no longer exists — will be created fresh when user opens Tabvana
        logger.debug('[Background] Stored main window not found, skipping restore');
    }
}

(async () => {
    try {
        logger.info('[Background] Starting initialization sequence');
        await clearAllProgress();
        logger.debug('[Background] Cleared all progress');
        
        registerDBSyncListeners();
        logger.debug('[Background] Registered DB sync listeners');
        
        await syncInitialState(); // Added await here to catch errors
        logger.debug('[Background] Synced initial state');
        
        initQueueProcessor();
        logger.debug('[Background] Initialized queue processor');

        // Low-priority sweep for any pages that landed in DB without a title
        enqueueOperation('fetchMissingTitle', {}, 50).catch(e =>
            logger.warn('[Background] Failed to enqueue startup fetchMissingTitle sweep:', e)
        );

        // Low-priority sweep for pages that lack semantic embeddings — covers pages
        // that missed embedding generation due to a previous backoff event, crash,
        // or service-worker kill mid-batch. Deduplication in enqueueOperation ensures
        // this is a no-op if a bulk generateEmbedding op is already pending.
        enqueueOperation('generateEmbedding', {}, 30).catch(e =>
            logger.warn('[Background] Failed to enqueue startup generateEmbedding sweep:', e)
        );

        // After an extension reload the popup window stays open but its tab goes
        // blank. Re-navigate it to main.html so the UI reloads in place.
        maybeRestoreMainWindow().catch(e =>
            logger.warn('[Background] Failed to restore main window:', e)
        );

        logger.info('[Background] Initialization sequence completed successfully');

        if (import.meta.env.MODE === 'development') {
            startMcpDevClient();
        }

        // Diagnostic heartbeat: log memory and DB queue depth every 60 seconds.
        // Entries are persisted to db.logs and survive SW restarts, giving a trail of
        // memory usage and queue depth leading up to a crash. 60s frequency stays well
        // under the 10K-row log cap (~1440 entries/day).
        setInterval(async () => {
            try {
                // chrome.system.memory is the correct API for extension service workers;
                // performance.memory is Window-only and always undefined in SW contexts.
                let memStr = 'n/a';
                try {
                    const info = await chrome.system.memory.getInfo();
                    const freeMB = Math.round(info.availableCapacity / 1024 / 1024);
                    const totalMB = Math.round(info.capacity / 1024 / 1024);
                    memStr = `${freeMB}/${totalMB}MB free`;
                } catch (_) {}
                const allPending = await db.operations.where('status').equals('pending').toArray();
                const pendingOps = allPending.length;
                const pendingByType: Record<string, number> = {};
                for (const op of allPending) { pendingByType[op.type] = (pendingByType[op.type] || 0) + 1; }
                const activeProgress = await db.progress.count();
                const pendingDetail = pendingOps > 0 ? ` (${JSON.stringify(pendingByType)})` : '';
                logger.info(
                    `[Heartbeat] mem=${memStr} pendingOps=${pendingOps}${pendingDetail} activeProgress=${activeProgress} queueProcessing=${isQueueProcessing()}`
                );
            } catch (_e) {
                // Non-fatal; never crash the SW over a diagnostic failure.
            }
        }, 60_000);
    } catch (e: any) {
        logger.error('[Background] CRITICAL ERROR during initialization:', e);
    }
})();

self.addEventListener('unhandledrejection', (event: any) => {
    logger.error('[Background SW] Unhandled Promise Rejection:', event.reason);
});

self.addEventListener('error', (event: any) => {
    logger.error('[Background SW] Uncaught Error:', event.error || event.message);
});

// Init prefs
loadPrefsFromDB();

// Listen for updates from options page and content scripts
browser.runtime.onMessage.addListener((message) => {
  try {
    if (message === 'reloadPrefs') {
      logger.info('[Background] Received reloadPrefs message');
      loadPrefsFromDB();
    } else if ((message as any)?.type === 'titleOverride') {
      const { url, title } = message as unknown as { type: string; url: string; title: string };
      if (url && title) {
        (async () => {
          try {
            const nurl = normalizeUrl(url);
            if (!shouldIgnore(nurl.normalized)) {
              setTitleOverride(nurl.normalized, title);
              // Update the DB immediately if the page is already tracked
              const existing = await db.pages.get(nurl.normalized);
              if (existing) {
                await db.pages.update(nurl.normalized, { title });
                logger.info('[Background] Applied title override:', nurl.normalized, '->', title);
              }
            }
          } catch (e: any) {
            logger.warn('[Background] Error applying title override:', e);
          }
        })();
      }
    }
  } catch (e: any) {
    logger.error('[Background] Error handling runtime message:', e, 'Message:', message);
  }
  return undefined;
});

// show welcome page on new install; create context menus
browser.runtime.onInstalled.addListener(async (details) => {
  try {
    logger.info(`[Background] Extension installed/updated. Reason: ${details.reason}`, details);
    if (details.reason === 'install') {
      const url = browser.runtime.getURL('src/welcome/welcome.html');
      await browser.tabs.create({ url });
    }
  } catch (e: any) {
    logger.error('[Background] Error during onInstalled handler:', e);
  }

  // (Re-)create context menus on install/update so they are always present.
  chrome.contextMenus.removeAll(() => {
    chrome.contextMenus.create({
      id: 'toggleSidebar',
      title: 'Toggle Tabvana Sidebar',
      contexts: ['all'],
    });
  });
});

// Track sidebar open state per window in memory.
// Using session storage required an async lookup before chrome.sidePanel.open(),
// which consumed the user gesture token before open() was called.
const sidebarState: Map<number, boolean> = new Map();

// Toggle the side panel open/closed when the context menu item is clicked.
//
// IMPORTANT: chrome.sidePanel.open() requires a user gesture. The user gesture
// token is only preserved through the current microtask chain — any await
// before open() can cause Chrome to reject the call as "not from a user gesture".
// We track open/close state in a synchronous Map so that open() is called at
// the very first await in the open branch, with no prior async calls.
chrome.contextMenus.onClicked.addListener(async (info, tab) => {
  if (info.menuItemId !== 'toggleSidebar' || !tab?.id || !tab?.windowId) return;

  const windowId = tab.windowId;
  const tabId = tab.id;
  const isOpen = sidebarState.get(windowId) ?? false;

  if (isOpen) {
    // No user gesture needed to close; await is fine here.
    await chrome.sidePanel.setOptions({ tabId, enabled: false });
    sidebarState.set(windowId, false);
    logger.info('[Background] Sidebar closed for tab', tabId);
  } else {
    // setOptions fire-and-forget (no gesture needed), then open() is the
    // first await — user gesture token is still alive at this point.
    chrome.sidePanel.setOptions({ tabId, enabled: true, path: 'src/sidebar/sidebar.html' });
    await chrome.sidePanel.open({ windowId });
    sidebarState.set(windowId, true);
    logger.info('[Background] Sidebar opened for window', windowId);
  }
});


// Fix for "The service worker navigation preload request was cancelled..." warning.
// We don't use navigation preload, so we disable it to prevent the warning and wasted requests.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
self.addEventListener('activate', (event: any) => {
  event.waitUntil(
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (self.registration?.navigationPreload) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          await self.registration.navigationPreload.disable();
        }
      } catch (e: any) {
        // Non-fatal: log but don't let this crash the service worker activation.
        logger.warn('[Background SW] Failed to disable navigation preload:', e);
      }
    })()
  );
});

// Open main window when extension icon is clicked
browser.action.onClicked.addListener(() => {
    try {
        logger.info('[Background] Action clicked, opening Tabvana');
        openTabvana();
    } catch(e: any) {
        logger.error('[Background] Error opening Tabvana from action click:', e);
    }
});

export {};
